/**
 * What the platform spends on models.
 *
 * The arithmetic is the backend's; this is the part that decides what a reader
 * is shown and in what order. Everything here is pure, so the page is a
 * rendering of tested decisions rather than a place where they get made.
 */

export interface Bucket {
  key: string;
  calls: number;
  costEur: number;
  inputTokens: number;
  outputTokens: number;
  failures: number;
}

export interface InstitutionBucket extends Bucket {
  ratio: number;
}

export interface ModelHealth {
  model: string;
  calls: number;
  errorRate: number;
  p50: number;
  p90: number;
}

export interface BudgetAlert {
  institutionId: string;
  spentEur: number;
  ratio: number;
  level: "approaching" | "exhausted";
}

export interface Outlier {
  institutionId: string;
  costEur: number;
  medianEur: number;
  factor: number;
}

export interface CostReport {
  month: string;
  calls: number;
  costEur: number;
  inputTokens: number;
  outputTokens: number;
  failureRate: number;
  latency: { p50: number; p90: number; p99: number };
  byFeature: Bucket[];
  byModel: Bucket[];
  byInstitution: InstitutionBucket[];
  health: ModelHealth[];
  alerts: BudgetAlert[];
  outliers: Outlier[];
  fallback: { calls: number; failures: number; rate: number };
  budgetEur: number;
}

/** The three shapes a model call may carry, as a reader knows them. */
export const FEATURE_KEY: Record<string, string> = {
  authoring: "ai.feature.authoring",
  importMap: "ai.feature.importMap",
  statsSummary: "ai.feature.statsSummary",
};

export function featureKey(key: string): string {
  return FEATURE_KEY[key] ?? key;
}

/**
 * A bucket's share of the total.
 *
 * Takes the cost rather than the bucket: the tables render rows that carry a
 * label as well, and a signature demanding the whole record would have every
 * call site constructing one.
 */
export function share(costEur: number, total: number): number {
  return total > 0 ? costEur / total : 0;
}

export const ALERT_KEY: Record<BudgetAlert["level"], string> = {
  approaching: "ai.alert.approaching",
  exhausted: "ai.alert.exhausted",
};

export const ALERT_TONE: Record<BudgetAlert["level"], string> = {
  approaching: "text-cue-amber",
  exhausted: "text-accent",
};

/**
 * Whether the provider looks unwell.
 *
 * One in twenty calls failing is a bad afternoon; one in five is an incident.
 * The number matters less than having one at all: without a threshold, a
 * provider degrading slowly is a thing everybody notices too late.
 */
export const UNHEALTHY_ERROR_RATE = 0.2;

export function isUnhealthy(health: ModelHealth): boolean {
  return health.calls > 0 && health.errorRate >= UNHEALTHY_ERROR_RATE;
}

/**
 * Whether the heuristic mapper is visibly carrying imports.
 *
 * Not an error state — it is the fallback working. It is shown because an
 * import that quietly succeeded on the heuristics for two days is how nobody
 * notices the model has been down.
 */
export const FALLBACK_NOTABLE_RATE = 0.1;

export function fallbackIsNotable(fallback: CostReport["fallback"]): boolean {
  return fallback.calls > 0 && fallback.rate >= FALLBACK_NOTABLE_RATE;
}

/** The months a reader may look at: this one and the eleven before it. */
export function recentMonths(today: string, count = 12): string[] {
  const [year, month] = today.slice(0, 7).split("-").map(Number) as [number, number];
  const months: string[] = [];

  for (let back = 0; back < count; back += 1) {
    const index = month - back;
    const shiftedYear = year + Math.floor((index - 1) / 12);
    const shiftedMonth = ((((index - 1) % 12) + 12) % 12) + 1;
    months.push(`${shiftedYear}-${String(shiftedMonth).padStart(2, "0")}`);
  }
  return months;
}
